/**
 * One ordering rule for every list. A field is read as a dotted path on the row. Empty values
 * (null, "", "—") go last; ISO dates newest first; numbers and number-led text ("12 files",
 * "84.2%", "$12.40") largest first; other text A to Z, natural ("v2" before "v10").
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T|$)/;
const NUMBER_LED = /^[$€£]?\s*(-?\d[\d,]*(?:\.\d+)?)/;

function read(row: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((value, part) => (value && typeof value === 'object' ? (value as Record<string, unknown>)[part] : undefined), row);
}
const isEmpty = (value: unknown) => value == null || value === '' || value === '—';

export function compareCells(a: unknown, b: unknown): number {
  const emptyA = isEmpty(a), emptyB = isEmpty(b);
  if (emptyA || emptyB) return emptyA === emptyB ? 0 : emptyA ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return b - a;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(b) - Number(a);
  const sa = String(a), sb = String(b);
  if (ISO_DATE.test(sa) && ISO_DATE.test(sb)) return sb.localeCompare(sa);
  const na = NUMBER_LED.exec(sa), nb = NUMBER_LED.exec(sb);
  if (na && nb) return Number(nb[1]!.replaceAll(',', '')) - Number(na[1]!.replaceAll(',', ''));
  return sa.localeCompare(sb, 'en', { numeric: true, sensitivity: 'base' });
}

/** Sorts a copy of `rows` by `field`; 'newest' (or an empty field) keeps the platform's order. */
export function sortRows<T>(rows: T[], field: string | undefined): T[] {
  if (!field || field === 'newest') return rows;
  return [...rows].sort((a, b) => compareCells(read(a, field), read(b, field)));
}
